import { useQuery } from '@tanstack/react-query';
import { useMetaIntegrationStatus, userProfileQueryOptions } from '@/lib/query-options';
import { derivePlatformConnections, type PlatformConnections } from '@/lib/platform-connections';
import { useTestMode } from '@/hooks/useTestMode';

export type { PlatformConnections };

/**
 * Per-platform connection signals for the campaigns surface (#5937).
 *
 * One source of truth so the campaigns index, sidebar, detail panels and the creation wizard
 * all agree on what "connected" means per platform — the same derivations Settings >
 * Integrations uses (integration-settings.tsx), lifted out so campaign surfaces can gate
 * per-platform instead of walling the whole page behind Google (the #5937 bug).
 *
 * The actual semantics live in ``derivePlatformConnections`` (lib/platform-connections.ts),
 * kept pure so they are unit-tested; this hook only wires the queries in.
 */
export function usePlatformConnections(): PlatformConnections {
  // #6257: Meta surfaces are gated behind test mode until Meta App Review clears; the status
  // query stays off outside it so gated users never hit /meta/* endpoints.
  const metaEnabled = useTestMode();
  const { data: userProfile, isLoading: isProfileLoading } = useQuery(userProfileQueryOptions());
  const { data: metaStatus, isLoading: isMetaStatusLoading } = useMetaIntegrationStatus({ enabled: metaEnabled });

  return derivePlatformConnections({
    userProfile,
    isProfileLoading,
    metaEnabled,
    metaStatus,
    isMetaStatusLoading,
  });
}
